import apiClient from '@/api/client';
import { EVENTS } from '@/api/endpoints';
import type { PaginatedResponse } from '@/api/types/common.types';
import type {
  CreateEventPhaseRequest,
  CreateEventRequest,
  EventDetail,
  EventFilters,
  EventListItem,
  EventPhase,
  UpdateEventPhaseRequest,
  UpdateEventRequest,
} from '@/api/types/event.types';

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** Fetch paginated list of events with optional filters. */
export async function getEvents(
  params?: EventFilters,
): Promise<PaginatedResponse<EventListItem>> {
  const { data } = await apiClient.get<PaginatedResponse<EventListItem>>(
    EVENTS.LIST,
    { params },
  );
  return data;
}

/** Fetch a single event by ID, including nested phases. */
export async function getEvent(id: string): Promise<EventDetail> {
  const { data } = await apiClient.get<EventDetail>(EVENTS.DETAIL(id));
  return data;
}

/** Create a new event. */
export async function createEvent(payload: CreateEventRequest): Promise<EventDetail> {
  const { data } = await apiClient.post<EventDetail>(EVENTS.CREATE, payload);
  return data;
}

/** Update an existing event (partial). */
export async function updateEvent(
  id: string,
  payload: UpdateEventRequest,
): Promise<EventDetail> {
  const { data } = await apiClient.patch<EventDetail>(EVENTS.UPDATE(id), payload);
  return data;
}

/** Delete an event. */
export async function deleteEvent(id: string): Promise<void> {
  await apiClient.delete(EVENTS.DELETE(id));
}

// ---------------------------------------------------------------------------
// Event Phases
// ---------------------------------------------------------------------------

/** Fetch all phases for an event. */
export async function getEventPhases(eventId: string): Promise<EventPhase[]> {
  const { data } = await apiClient.get<EventPhase[]>(EVENTS.PHASES(eventId));
  return data;
}

/** Create a new phase for an event. */
export async function createEventPhase(
  eventId: string,
  payload: CreateEventPhaseRequest,
): Promise<EventPhase> {
  const { data } = await apiClient.post<EventPhase>(
    EVENTS.PHASES(eventId),
    payload,
  );
  return data;
}

/** Update an existing event phase (partial). */
export async function updateEventPhase(
  eventId: string,
  phaseId: string,
  payload: UpdateEventPhaseRequest,
): Promise<EventPhase> {
  const { data } = await apiClient.patch<EventPhase>(
    `${EVENTS.PHASES(eventId)}${phaseId}/`,
    payload,
  );
  return data;
}

/** Delete an event phase. */
export async function deleteEventPhase(
  eventId: string,
  phaseId: string,
): Promise<void> {
  await apiClient.delete(`${EVENTS.PHASES(eventId)}${phaseId}/`);
}
